import { errorResponseSchema } from '@app/shared';
import type { ZodType } from 'zod';

/** Erreur métier renvoyée par l'API : porte son code HTTP et le code stable du plan (`totp_required`, `loan_terms_locked`…). */
export class ApiRequestError extends Error {
  constructor(
    readonly statusCode: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface ApiFetchOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Valide et type la réponse. Omis pour un `204 No Content`. */
  schema?: ZodType<T>;
}

/** Client HTTP unique vers `/api` : cookie de session automatique (même origine), erreurs typées. */
export async function apiFetch<T = void>(path: string, options: ApiFetchOptions<T> = {}): Promise<T> {
  // Un `FormData` (dépôt de fichiers) part tel quel : le navigateur pose lui-même le `content-type` et la frontière.
  const { body } = options;
  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers: body === undefined || body instanceof FormData ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const data: unknown = await response.json().catch(() => undefined);

  if (!response.ok) {
    const parsed = errorResponseSchema.safeParse(data);
    if (parsed.success) throw new ApiRequestError(parsed.data.statusCode, parsed.data.code, parsed.data.message);
    throw new ApiRequestError(response.status, 'unknown_error', "Erreur inattendue de l'API");
  }

  return options.schema ? options.schema.parse(data) : (data as T);
}

/** Message à afficher pour une erreur de mutation : celui de l'API si on le connaît, sinon un repli générique. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback;
}
